import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  limit as fbLimit,
  where,
  documentId,
  getDocs,
} from 'firebase/firestore';
import { db } from '../firebase';

// ---------- Helpers ----------
const fmtDateTime = (ts) => {
  try {
    if (!ts) return '—';
    if (typeof ts?.toDate === 'function') return ts.toDate().toLocaleString();
    if (typeof ts === 'number') return new Date(ts).toLocaleString();
    return '—';
  } catch { return '—'; }
};

const chunk = (arr, size) => {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

const toPercent = (n) => {
  if (n == null) return null;
  const x = Number(n);
  if (!Number.isFinite(x)) return null;
  if (x <= 1) return `${Math.round(x * 100)}%`;
  if (x <= 100) return `${Math.round(x)}%`;
  return `${Math.round((x / 100) * 100)}%`;
};

const guessPhase = (r) => {
  const v =
    (r.phase || r.type || r.kind || '').toString().toLowerCase() ||
    (r.isPickup ? 'pickup' : '') ||
    (r.isReturn ? 'return' : '');
  if (v.includes('pickup')) return 'pickup';
  if (v.includes('return')) return 'return';
  return 'unknown';
};

const getImageSets = (r) => {
  const pickup = r.pickupImageUrls || r.beforeImages || [];
  const ret = r.returnImageUrls || r.afterImages || [];
  const generic = r.imageUrls || [];
  return { pickup: Array.isArray(pickup) ? pickup : [], ret: Array.isArray(ret) ? ret : [], generic: Array.isArray(generic) ? generic : [] };
};

// NEW: damage images (handles several possible shapes)
const getDamageImageUrls = (r) => {
  const a = r.damageImageUrls;
  const b = r.damage?.images;
  const c = r.damage?.photos;
  const d = r.returnDamageImageUrls;
  const e = r.pickupDamageImageUrls;
  const candidates = [a, b, c, d, e].filter(Boolean);
  for (const arr of candidates) if (Array.isArray(arr)) return arr;
  return [];
};

const getAttachmentSets = (r) => {
  const a = r.attachmentUrls || r.attachments || [];
  const p = r.pdfUrls || r.docUrls || [];
  return [...(Array.isArray(a) ? a : []), ...(Array.isArray(p) ? p : [])];
};

const looksDamaged = (r) => {
  const dmgUrls = getDamageImageUrls(r);
  return !!(
    r?.hasDamage ||
    (typeof r?.damageCount === 'number' && r.damageCount > 0) ||
    (r?.damageSeverity != null && r.damageSeverity !== '') ||
    (Array.isArray(r?.damageCategories) && r.damageCategories.length > 0) ||
    (dmgUrls && dmgUrls.length > 0) ||
    (r?.damageNotes && String(r.damageNotes).trim()) ||
    (r?.damageNote && String(r.damageNote).trim()) ||
    (r?.notes && /damage|scratch|dent|crack|stain/i.test(r.notes))
  );
};

// ---------- Small UI ----------
function KeyValue({ label, value }) {
  if (
    value == null ||
    value === '' ||
    (Array.isArray(value) && value.length === 0)
  ) return null;
  return (
    <div style={styles.kvRow}>
      <div style={styles.kvLabel}>{label}</div>
      <div style={styles.kvValue}>
        {Array.isArray(value) ? value.join(', ') : String(value)}
      </div>
    </div>
  );
}

function PhotoGrid({ urls }) {
  if (!urls?.length) return null;
  return (
    <div style={styles.images}>
      {urls.map((u, i) => (
        <a key={i} href={u} target="_blank" rel="noopener noreferrer" title="Open original">
          <img src={u} alt={`photo ${i + 1}`} style={styles.thumb} />
        </a>
      ))}
    </div>
  );
}

function AttachmentList({ urls }) {
  if (!urls?.length) return null;
  return (
    <div style={styles.attachments}>
      {urls.map((u, i) => (
        <a key={i} href={u} target="_blank" rel="noopener noreferrer" style={styles.attachment}>
          📄 Attachment {i + 1}
        </a>
      ))}
    </div>
  );
}

function ReportPanel({ title, report }) {
  if (!report) {
    return (
      <div style={styles.panel}>
        <div style={styles.panelTitle}>{title}</div>
        <div style={{ color: '#6b7280', fontSize: 14 }}>No report submitted.</div>
      </div>
    );
  }

  const { pickup, ret, generic } = getImageSets(report);
  const imgs = pickup?.length || ret?.length ? [...pickup, ...ret] : generic;
  const attach = getAttachmentSets(report);

  // Damage fields + photos
  const damageUrls = getDamageImageUrls(report);
  const damageNotes =
    report.damageNotes ||
    report.damageNote ||
    report.damages ||
    report.damage?.notes ||
    (report.notes && /damage|scratch|dent|crack|stain/i.test(report.notes) ? report.notes : null);

  const fuel = report.fuelLevel != null ? toPercent(report.fuelLevel) : null;
  const fuelStart = report.fuelStart != null ? toPercent(report.fuelStart) : null;
  const fuelEnd = report.fuelEnd != null ? toPercent(report.fuelEnd) : null;

  const checklist = report.checklist || report.items || report.checks || null;
  const checklistDisplay = Array.isArray(checklist)
    ? checklist
    : checklist && typeof checklist === 'object'
    ? Object.entries(checklist).filter(([, v]) => v).map(([k]) => k)
    : null;

  return (
    <div style={styles.panel}>
      <div style={styles.panelTitle}>{title}</div>

      <div style={styles.metaBlock}>
        <KeyValue label="Submitted" value={fmtDateTime(report.timestamp || report.createdAt)} />
        <KeyValue label="Submitted by" value={report.userEmail || report.submittedBy || report.userId} />
        <KeyValue label="Role" value={report.role || report.userRole} />
        <KeyValue label="Odometer" value={report.odometer ?? report.mileage} />
        <KeyValue label="Fuel" value={fuel || (fuelStart && fuelEnd ? `${fuelStart} → ${fuelEnd}` : null)} />
        <KeyValue label="Cleanliness" value={report.cleanliness} />
        <KeyValue label="Notes" value={report.notes} />
        <KeyValue label="Checklist" value={checklistDisplay} />
        {report.signatureUrl ? (
          <div style={{ marginTop: 8 }}>
            <div style={styles.kvLabel}>Signature</div>
            <a href={report.signatureUrl} target="_blank" rel="noreferrer">
              <img src={report.signatureUrl} alt="Signature" style={styles.signature} />
            </a>
          </div>
        ) : null}
      </div>

      {/* Condition photos */}
      <PhotoGrid urls={imgs} />
      <AttachmentList urls={attach} />

      {/* Damage section (if any) */}
      {(damageUrls.length || damageNotes || report.damageSeverity != null || (Array.isArray(report.damageCategories) && report.damageCategories.length)) ? (
        <div style={styles.damageBlock}>
          <div style={styles.subheading}>Damage</div>
          <KeyValue label="Damage Notes" value={damageNotes} />
          <KeyValue label="Severity" value={report.damageSeverity} />
          <KeyValue label="Categories" value={Array.isArray(report.damageCategories) ? report.damageCategories : null} />
          <PhotoGrid urls={damageUrls} />
        </div>
      ) : null}
    </div>
  );
}

function DeltaPanel({ pickup, ret }) {
  if (!pickup || !ret) return null;

  const deltas = [];
  if (pickup.odometer != null || ret.odometer != null) {
    deltas.push({ label: 'Odometer', value: `${pickup.odometer ?? '—'} → ${ret.odometer ?? '—'}` });
  }
  const fStart = pickup.fuelLevel ?? pickup.fuelStart;
  const fEnd = ret.fuelLevel ?? ret.fuelEnd;
  if (fStart != null || fEnd != null) {
    const a = toPercent(fStart) ?? '—';
    const b = toPercent(fEnd) ?? '—';
    deltas.push({ label: 'Fuel', value: `${a} → ${b}` });
  }

  // Checklist diff (object -> bools)
  const pc = (pickup.checklist && typeof pickup.checklist === 'object' && !Array.isArray(pickup.checklist)) ? pickup.checklist : null;
  const rc = (ret.checklist && typeof ret.checklist === 'object' && !Array.isArray(ret.checklist)) ? ret.checklist : null;
  if (pc && rc) {
    const changed = Object.keys({ ...pc, ...rc }).filter((k) => !!pc[k] !== !!rc[k]);
    if (changed.length) deltas.push({ label: 'Checklist changes', value: changed.join(', ') });
  }

  if (!deltas.length) return null;

  return (
    <div style={styles.deltaPanel}>
      <div style={styles.panelTitle}>Differences</div>
      {deltas.map((d, i) => (
        <div key={i} style={styles.kvRow}>
          <div style={styles.kvLabel}>{d.label}</div>
          <div style={styles.kvValue}>{d.value}</div>
        </div>
      ))}
    </div>
  );
}

// ---------- Main ----------
export default function AdminConditionReportViewer() {
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters (client-side)
  const [search, setSearch] = useState('');
  const [phaseFilter, setPhaseFilter] = useState('all'); // all | pickup | return
  const [hasPhotos, setHasPhotos] = useState(false);
  const [hasDamage, setHasDamage] = useState(false);

  // Enrichment
  const [bookingMeta, setBookingMeta] = useState({}); // bookingId -> { listingTitle, renterId, ownerId, listingId }
  const [usersMeta, setUsersMeta] = useState({});     // uid -> { email, displayName }

  // Load latest 200 reports live
  useEffect(() => {
    const q = query(collection(db, 'conditionReports'), orderBy('timestamp', 'desc'), fbLimit(200));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setReports(rows);
        setLoading(false);
      },
      (err) => {
        console.error('❌ conditionReports snapshot error:', err);
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  // Enrich bookings (batched)
  useEffect(() => {
    const ids = Array.from(new Set(reports.map((r) => r.bookingId).filter(Boolean)));
    if (!ids.length) return;
    (async () => {
      try {
        const out = {};
        for (const batch of chunk(ids, 10)) {
          const qs = query(collection(db, 'bookings'), where(documentId(), 'in', batch));
          const snap = await getDocs(qs);
          snap.forEach((doc) => {
            const d = doc.data();
            out[doc.id] = {
              listingTitle: d.listingTitle || d.title || 'Listing',
              renterId: d.renterId,
              ownerId: d.ownerId,
              listingId: d.listingId || doc.id,
            };
          });
        }
        setBookingMeta((m) => ({ ...m, ...out }));
      } catch (e) {
        console.warn('⚠️ enrich bookings failed:', e);
      }
    })();
  }, [reports]);

  // Enrich users (renter/owner + submitters)
  useEffect(() => {
    const uids = new Set();
    Object.values(bookingMeta).forEach((m) => {
      if (m?.renterId) uids.add(m.renterId);
      if (m?.ownerId) uids.add(m.ownerId);
    });
    reports.forEach((r) => { if (r.userId) uids.add(r.userId); });

    const want = Array.from(uids).filter((uid) => uid && !usersMeta[uid]);
    if (!want.length) return;

    (async () => {
      try {
        const out = {};
        for (const batch of chunk(want, 10)) {
          const qs = query(collection(db, 'users'), where(documentId(), 'in', batch));
          const snap = await getDocs(qs);
          snap.forEach((doc) => {
            const d = doc.data();
            out[doc.id] = {
              email: d.email || d.username || d.contactEmail || '',
              displayName: d.displayName || d.name || '',
            };
          });
        }
        setUsersMeta((m) => ({ ...m, ...out }));
      } catch (e) {
        console.warn('⚠️ enrich users failed:', e);
      }
    })();
  }, [bookingMeta, reports, usersMeta]);

  // Group by booking and determine pickup/return
  const grouped = useMemo(() => {
    const map = {};
    for (const r of reports) {
      const bid = r.bookingId || '_unknown';
      if (!map[bid]) map[bid] = { all: [], pickup: null, return: null };
      const ph = guessPhase(r);
      map[bid].all.push(r);
      if (ph === 'pickup') {
        if (!map[bid].pickup || (r.timestamp?.toMillis?.() || 0) > (map[bid].pickup?.timestamp?.toMillis?.() || 0)) {
          map[bid].pickup = r;
        }
      } else if (ph === 'return') {
        if (!map[bid].return || (r.timestamp?.toMillis?.() || 0) > (map[bid].return?.timestamp?.toMillis?.() || 0)) {
          map[bid].return = r;
        }
      }
    }
    // fallback if no explicit phase
    for (const bid of Object.keys(map)) {
      const list = map[bid].all
        .slice()
        .sort((a, b) => (b.timestamp?.toMillis?.() || 0) - (a.timestamp?.toMillis?.() || 0));
      if (!map[bid].pickup && list.length) map[bid].pickup = list[list.length - 1];
      if (!map[bid].return && list.length) map[bid].return = list[0];
    }
    return map;
  }, [reports]);

  // Booking-level filtering (fast, no new indexes)
  const filteredBookingIds = useMemo(() => {
    let ids = Object.keys(grouped);

    if (search.trim()) {
      const s = search.toLowerCase();
      ids = ids.filter((bid) => {
        const meta = bookingMeta[bid] || {};
        const title = (meta.listingTitle || '').toLowerCase();
        const renter = (meta.renterId || '').toLowerCase();
        const owner = (meta.ownerId || '').toLowerCase();
        const listingId = (meta.listingId || '').toLowerCase();

        const anyReport = grouped[bid].all.some((r) => {
          return (
            (r.bookingId || '').toLowerCase().includes(s) ||
            (r.listingId || '').toLowerCase().includes(s) ||
            (r.userId || '').toLowerCase().includes(s) ||
            title.includes(s) ||
            renter.includes(s) ||
            owner.includes(s) ||
            listingId.includes(s) ||
            (usersMeta[r.userId]?.email || '').toLowerCase().includes(s)
          );
        });
        return anyReport;
      });
    }

    if (phaseFilter !== 'all') {
      ids = ids.filter((bid) => !!grouped[bid][phaseFilter]);
    }

    if (hasPhotos) {
      ids = ids.filter((bid) => {
        const { pickup, return: ret } = grouped[bid];
        const p = pickup && (getImageSets(pickup).pickup.length || getImageSets(pickup).ret.length || getImageSets(pickup).generic.length);
        const r = ret && (getImageSets(ret).pickup.length || getImageSets(ret).ret.length || getImageSets(ret).generic.length);
        return p || r;
      });
    }

    if (hasDamage) {
      ids = ids.filter((bid) => {
        const { pickup, return: ret } = grouped[bid];
        return looksDamaged(pickup) || looksDamaged(ret);
      });
    }

    return ids;
  }, [grouped, bookingMeta, usersMeta, search, phaseFilter, hasPhotos, hasDamage]);

  // Flat list of filtered reports (for CSV export)
  const filteredReportsFlat = useMemo(() => {
    const keepBids = new Set(filteredBookingIds);
    let arr = reports.filter((r) => keepBids.has(r.bookingId || '_unknown'));

    if (phaseFilter !== 'all') {
      arr = arr.filter((r) => guessPhase(r) === phaseFilter);
    }
    if (hasPhotos) {
      arr = arr.filter((r) => {
        const { pickup, ret, generic } = getImageSets(r);
        return pickup.length || ret.length || generic.length;
      });
    }
    if (hasDamage) {
      arr = arr.filter((r) => looksDamaged(r));
    }
    return arr;
  }, [reports, filteredBookingIds, phaseFilter, hasPhotos, hasDamage]);

  // CSV export (filtered rows)
  const csv = useMemo(() => {
    const header = [
      'timeISO',
      'bookingId',
      'listingId',
      'listingTitle',
      'phase',
      'submittedByUid',
      'submittedByEmail',
      'role',
      'renterId',
      'renterEmail',
      'ownerId',
      'ownerEmail',
      'odometer',
      'fuelStart_or_level',
      'fuelEnd',
      'hasDamage',
      'damageSeverity',
      'damageCategories',
      'damagePhotoCount',
      'damageNotes',
      'photoCount',
      'attachmentCount',
      'signaturePresent',
      'notes',
    ].join(',');

    const rows = filteredReportsFlat.map((r) => {
      const meta = bookingMeta[r.bookingId] || {};
      const renter = usersMeta[meta.renterId] || {};
      const owner = usersMeta[meta.ownerId] || {};
      const submitter = usersMeta[r.userId] || {};

      const phase = guessPhase(r);
      const imgs = getImageSets(r);
      const attachments = getAttachmentSets(r);
      const damageUrls = getDamageImageUrls(r);

      const photoCount =
        (imgs.pickup?.length || 0) + (imgs.ret?.length || 0) + (imgs.generic?.length || 0);
      const damagePhotoCount = damageUrls.length;

      const timeISO =
        r.timestamp?.toDate?.().toISOString?.() ||
        (typeof r.timestamp === 'number' ? new Date(r.timestamp).toISOString() : '');

      const fuelStartOrLevel = r.fuelStart ?? r.fuelLevel ?? '';
      const fuelEnd = r.fuelEnd ?? '';

      const safe = (x) =>
        `"${String(x ?? '')
          .replace(/"/g, '""')
          .replace(/\n/g, ' ')
          .replace(/\r/g, ' ')}"`;

      return [
        timeISO,
        r.bookingId || '',
        r.listingId || meta.listingId || '',
        (meta.listingTitle || 'Listing').replace(/,/g, ' '),
        phase,
        r.userId || '',
        r.userEmail || submitter.email || '',
        r.role || r.userRole || '',
        meta.renterId || '',
        renter.email || '',
        meta.ownerId || '',
        owner.email || '',
        r.odometer ?? r.mileage ?? '',
        fuelStartOrLevel,
        fuelEnd,
        looksDamaged(r) ? 'yes' : 'no',
        r.damageSeverity ?? '',
        Array.isArray(r.damageCategories) ? safe(r.damageCategories.join('|')) : '',
        damagePhotoCount,
        safe(r.damageNotes || r.damageNote || ''),
        photoCount,
        attachments.length || 0,
        r.signatureUrl ? 'yes' : 'no',
        safe(r.notes || ''),
      ].join(',');
    });

    return [header, ...rows].join('\n');
  }, [filteredReportsFlat, bookingMeta, usersMeta]);

  const downloadCSV = () => {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `condition-reports-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div style={styles.container}>
      <button onClick={() => navigate('/dashboard')} style={styles.backButton}>
        ← Back to Dashboard
      </button>

      <div style={styles.headerRow}>
        <h2 style={styles.title}>📸 Condition Reports (latest 200)</h2>
        <div style={styles.filters}>
          <input
            style={styles.input}
            placeholder="Search booking/listing/user/email"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select style={styles.input} value={phaseFilter} onChange={(e) => setPhaseFilter(e.target.value)}>
            <option value="all">All phases</option>
            <option value="pickup">Pickup only</option>
            <option value="return">Return only</option>
          </select>
          <label style={styles.chkLabel}>
            <input type="checkbox" checked={hasPhotos} onChange={(e) => setHasPhotos(e.target.checked)} /> Photos
          </label>
          <label style={styles.chkLabel}>
            <input type="checkbox" checked={hasDamage} onChange={(e) => setHasDamage(e.target.checked)} /> Has damage
          </label>
          <button onClick={downloadCSV} style={styles.secondaryBtn}>Export CSV (filtered)</button>
        </div>
      </div>

      {loading ? (
        <p>Loading reports…</p>
      ) : filteredBookingIds.length === 0 ? (
        <p>No reports found.</p>
      ) : (
        <div style={styles.grid}>
          {filteredBookingIds.map((bid) => {
            const grp = grouped[bid];
            const meta = bookingMeta[bid] || {};
            const renter = usersMeta[meta.renterId] || {};
            const owner = usersMeta[meta.ownerId] || {};

            const pImgs = grp.pickup ? getImageSets(grp.pickup) : null;
            const rImgs = grp.return ? getImageSets(grp.return) : null;
            const photoCount =
              (pImgs ? (pImgs.pickup.length + pImgs.ret.length + pImgs.generic.length) : 0) +
              (rImgs ? (rImgs.pickup.length + rImgs.ret.length + rImgs.generic.length) : 0);

            return (
              <div key={bid} style={styles.card}>
                <div style={styles.cardHeader}>
                  <div>
                    <div style={styles.cardTitle}>{meta.listingTitle || 'Listing'}</div>
                    <div style={styles.cardSub}>
                      <span title={bid}><strong>Booking:</strong> {bid.slice(0, 8)}…</span> &nbsp;•&nbsp;
                      <span title={meta.listingId}><strong>Listing:</strong> {(meta.listingId || '—').slice(0, 8)}…</span> &nbsp;•&nbsp;
                      <span><strong>Photos:</strong> {photoCount}</span>
                    </div>
                    <div style={styles.cardSub}>
                      <strong>Renter:</strong> {meta.renterId || '—'} {renter.email ? `(${renter.email})` : ''} &nbsp;•&nbsp;
                      <strong>Owner:</strong> {meta.ownerId || '—'} {owner.email ? `(${owner.email})` : ''}
                    </div>
                  </div>
                  <div style={styles.idBtns}>
                    <button
                      style={styles.smallBtn}
                      onClick={() => navigator.clipboard?.writeText(bid)}
                      title="Copy bookingId"
                    >
                      Copy ID
                    </button>
                    <button
                      style={styles.smallBtn}
                      onClick={() => navigate(`/bookings?bookingId=${encodeURIComponent(bid)}`)}
                      title="Open booking"
                    >
                      Open Booking
                    </button>
                  </div>
                </div>

                <div style={styles.twoCol}>
                  <ReportPanel title="Pickup" report={grp.pickup} />
                  <ReportPanel title="Return" report={grp.return} />
                </div>

                <DeltaPanel pickup={grp.pickup} ret={grp.return} />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---------- Styles ----------
const styles = {
  container: {
    padding: '2rem',
    fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
    backgroundColor: '#f9fafb',
    minHeight: '100vh',
  },
  backButton: {
    backgroundColor: '#eeeeee',
    border: '1px solid #ccc',
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: 500,
    marginBottom: '1rem',
  },
  headerRow: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 12,
    flexWrap: 'wrap',
  },
  title: {
    fontSize: '1.5rem',
    fontWeight: 700,
    color: '#065f46',
    margin: 0,
  },
  filters: { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' },
  input: {
    padding: '8px 10px',
    borderRadius: 6,
    border: '1px solid #d1d5db',
    minWidth: 180,
    background: '#fff',
    fontSize: 14,
  },
  chkLabel: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, color: '#374151' },

  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(680px, 1fr))',
    gap: '1rem',
  },
  card: {
    backgroundColor: '#fff',
    borderRadius: 10,
    padding: '1rem',
    border: '1px solid #e5e7eb',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  cardHeader: { display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 10 },
  cardTitle: { fontWeight: 700, color: '#111827' },
  cardSub: { fontSize: 13, color: '#374151', marginTop: 2 },
  idBtns: { display: 'flex', gap: 8 },
  smallBtn: {
    padding: '4px 8px',
    fontSize: 12,
    borderRadius: 6,
    border: '1px solid #d1d5db',
    background: '#fff',
    cursor: 'pointer',
  },

  twoCol: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 12,
    alignItems: 'start',
  },
  panel: {
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    padding: 12,
    background: '#fcfffe',
  },
  panelTitle: { fontWeight: 600, color: '#065f46', marginBottom: 8 },

  metaBlock: { marginBottom: 8 },
  kvRow: { display: 'flex', gap: 12, marginBottom: 6, fontSize: 14, lineHeight: 1.25 },
  kvLabel: { width: 130, color: '#6b7280' },
  kvValue: { flex: 1, color: '#111827', wordBreak: 'break-word' },

  images: { display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 },
  thumb: {
    width: 84,
    height: 84,
    objectFit: 'cover',
    borderRadius: 6,
    border: '1px solid #e5e7eb',
    background: '#f8fafc',
    transition: 'transform 0.15s ease',
  },

  attachments: { display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 },
  attachment: {
    fontSize: 13,
    padding: '6px 8px',
    border: '1px solid #d1d5db',
    borderRadius: 6,
    background: '#fff',
    color: '#065f46',
    textDecoration: 'none',
  },

  signature: {
    maxWidth: 160,
    maxHeight: 80,
    border: '1px solid #e5e7eb',
    borderRadius: 6,
    background: '#fff',
  },

  deltaPanel: {
    marginTop: 12,
    borderTop: '1px dashed #e5e7eb',
    paddingTop: 10,
  },

  // NEW: Damage section style
  damageBlock: {
    marginTop: 10,
    paddingTop: 10,
    borderTop: '1px dashed #e5e7eb',
  },
  subheading: {
    fontWeight: 600,
    color: '#b91c1c',
    marginBottom: 6,
  },
};
